/**
 * Support: the WhatsApp button, "Report a problem", the crash screen, and the
 * hint under each screen's empty state (what the screen is for, in a line).
 */
const support = {
  en: {
    whatsapp: "Chat with us on WhatsApp",
    whatsappMessage: "Hello, I am {shop} ({slug}). I need help.",
    needHand: "Need a hand?",
    reportOpen: "Report a problem",
    reportTitle: "Report a problem",
    reportIntro: "Tell us what went wrong. We receive the page you are on and the last error, so you do not need to describe it technically.",
    reportPlaceholder: "What happened? What were you trying to do?",
    reportSend: "Send",
    reportSending: "Sending…",
    reportCancel: "Cancel",
    reportThanks: "Thank you — we received your report and will get back to you.",
    reportFailed: "Could not send it. Please try again, or message us on WhatsApp.",
    reportShort: "Please write a few words.",
    errTitle: "Something went wrong",
    errBody: "This screen hit a problem. Your data is safe. Reload the page, or tell us what happened.",
    errReload: "Reload",
    errHome: "Back to the start",
    learnMore: "Learn more",
    empty: {
      cash: "The drawer's history appears here once you count it open at the register and close it at the end of the day.",
      deliveries: "Record what arrives from a supplier: your stock goes up at once, and you can add the price later.",
      packs: "A pack sells several products together for one price. Create one to offer a bundle at the register and on your menu.",
      reminders: "A reminder notifies you at a time you choose — for example, to restock every Monday morning.",
      savings: "The cash you take home when you close the drawer, and any money you put aside, is tracked here.",
      stock: "Nothing matches this filter. Products appear here as soon as you add them.",
      suppliers: "Add the people you buy from to see what you owe them and what each delivery cost.",
      expenses: "Rent, bills, salaries: note what the shop spends so the profit you see is the real one.",
      offers: "An offer takes a percentage or an amount off chosen products. Create one to bring customers in.",
      preorders: "When customers reserve products from your menu, their pre-orders show up here.",
      journal: "Every sale, refund, price change and cash movement is written here, with who did it.",
      materials: "Materials are what you mix with: bases, flavours, nicotine boosters, bottles. Add them to start mixing.",
      recipes: "A recipe says which flavours go into a juice, and how much of each.",
      credit: "Customers who owe you money show up here. You can sell on credit from the register.",
      purchases: "Your sales are listed here after the first sale at the register.",
      batches: "Batches you have mixed appear here, with their cost per bottle.",
    },
  },
  fr: {
    whatsapp: "Discuter avec nous sur WhatsApp",
    whatsappMessage: "Bonjour, je suis {shop} ({slug}). J'ai besoin d'aide.",
    needHand: "Besoin d'un coup de main ?",
    reportOpen: "Signaler un problème",
    reportTitle: "Signaler un problème",
    reportIntro: "Dites-nous ce qui s'est passé. Nous recevons la page où vous êtes et la dernière erreur, inutile de la décrire techniquement.",
    reportPlaceholder: "Que s'est-il passé ? Que vouliez-vous faire ?",
    reportSend: "Envoyer",
    reportSending: "Envoi…",
    reportCancel: "Annuler",
    reportThanks: "Merci — nous avons bien reçu votre signalement et reviendrons vers vous.",
    reportFailed: "Envoi impossible. Réessayez, ou écrivez-nous sur WhatsApp.",
    reportShort: "Écrivez quelques mots, s'il vous plaît.",
    errTitle: "Un problème est survenu",
    errBody: "Cet écran a rencontré un problème. Vos données sont en sécurité. Rechargez la page, ou dites-nous ce qui s'est passé.",
    errReload: "Recharger",
    errHome: "Retour à l'accueil",
    learnMore: "En savoir plus",
    empty: {
      cash: "L'historique de la caisse apparaît ici dès que vous la comptez à l'ouverture et la fermez en fin de journée.",
      deliveries: "Enregistrez ce que vous livre un fournisseur : le stock augmente aussitôt, et vous pouvez ajouter le prix plus tard.",
      packs: "Un pack vend plusieurs produits ensemble pour un seul prix. Créez-en un pour proposer un lot à la caisse et sur votre menu.",
      reminders: "Un rappel vous prévient à l'heure de votre choix — par exemple pour réapprovisionner chaque lundi matin.",
      savings: "Le cash que vous rapportez en fermant la caisse, et l'argent que vous mettez de côté, sont suivis ici.",
      stock: "Rien ne correspond à ce filtre. Les produits apparaissent ici dès que vous les ajoutez.",
      suppliers: "Ajoutez ceux chez qui vous achetez pour voir ce que vous leur devez et ce que chaque livraison a coûté.",
      expenses: "Loyer, factures, salaires : notez ce que dépense la boutique pour que le bénéfice affiché soit le vrai.",
      offers: "Une offre retire un pourcentage ou un montant sur des produits choisis. Créez-en une pour attirer des clients.",
      preorders: "Quand des clients réservent des produits depuis votre menu, leurs précommandes apparaissent ici.",
      journal: "Chaque vente, remboursement, changement de prix et mouvement de caisse est écrit ici, avec son auteur.",
      materials: "Les matières sont ce avec quoi vous mélangez : bases, arômes, boosters de nicotine, flacons. Ajoutez-les pour commencer à mélanger.",
      recipes: "Une recette indique quels arômes entrent dans un jus, et en quelle quantité.",
      credit: "Les clients qui vous doivent de l'argent apparaissent ici. Vous pouvez vendre à crédit depuis la caisse.",
      purchases: "Vos ventes sont listées ici après la première vente à la caisse.",
      batches: "Les lots que vous avez mélangés apparaissent ici, avec leur coût par flacon.",
    },
  },
  ar: {
    whatsapp: "تحدّث معنا على واتساب",
    whatsappMessage: "مرحباً، أنا {shop} ({slug}). أحتاج إلى مساعدة.",
    needHand: "هل تحتاج إلى مساعدة؟",
    reportOpen: "الإبلاغ عن مشكلة",
    reportTitle: "الإبلاغ عن مشكلة",
    reportIntro: "أخبرنا بما حدث. نستلم الصفحة التي كنت فيها وآخر خطأ، فلا حاجة لوصفه تقنياً.",
    reportPlaceholder: "ماذا حدث؟ وماذا كنت تحاول أن تفعل؟",
    reportSend: "إرسال",
    reportSending: "جارٍ الإرسال…",
    reportCancel: "إلغاء",
    reportThanks: "شكراً — وصلنا بلاغك وسنعود إليك.",
    reportFailed: "تعذّر الإرسال. حاول مجدداً أو راسلنا على واتساب.",
    reportShort: "اكتب بضع كلمات من فضلك.",
    errTitle: "حدث خطأ ما",
    errBody: "واجهت هذه الشاشة مشكلة. بياناتك بأمان. أعد تحميل الصفحة أو أخبرنا بما حدث.",
    errReload: "إعادة التحميل",
    errHome: "العودة إلى البداية",
    learnMore: "اعرف المزيد",
    empty: {
      cash: "يظهر سجل الصندوق هنا بعد أن تعدّه عند الفتح وتغلقه في نهاية اليوم.",
      deliveries: "سجّل ما يصلك من المورّد: يزيد المخزون فوراً، ويمكنك إضافة السعر لاحقاً.",
      packs: "تبيع الباقة عدة منتجات معاً بسعر واحد. أنشئ واحدة لتعرضها عند البيع وفي قائمتك.",
      reminders: "ينبّهك التذكير في الوقت الذي تختاره، مثلاً لإعادة التزوّد كل صباح اثنين.",
      savings: "يُتابَع هنا النقد الذي تأخذه معك عند إغلاق الصندوق وأي مبلغ تضعه جانباً.",
      stock: "لا شيء يطابق هذا الفلتر. تظهر المنتجات هنا بمجرد إضافتها.",
      suppliers: "أضف من تشتري منهم لترى ما عليك لهم وكم كلّفت كل توريدة.",
      expenses: "الإيجار والفواتير والأجور: دوّن ما ينفقه المتجر ليكون الربح الذي تراه هو الحقيقي.",
      offers: "يخصم العرض نسبة أو مبلغاً من منتجات تختارها. أنشئ عرضاً لجذب الزبائن.",
      preorders: "عندما يحجز الزبائن منتجات من قائمتك تظهر طلباتهم المسبقة هنا.",
      journal: "كل عملية بيع أو استرجاع أو تغيير سعر أو حركة نقد تُسجَّل هنا مع اسم من قام بها.",
      materials: "المواد هي ما تمزج به: القواعد والنكهات ومعزّزات النيكوتين والقوارير. أضفها لتبدأ المزج.",
      recipes: "تحدد الوصفة النكهات الداخلة في العصير وكمية كل منها.",
      credit: "يظهر هنا الزبائن الذين عليهم لك مال. يمكنك البيع بالدين من نقطة البيع.",
      purchases: "تظهر مبيعاتك هنا بعد أول عملية بيع في نقطة البيع.",
      batches: "تظهر هنا الدفعات التي مزجتها مع تكلفة القارورة الواحدة.",
    },
  },
};

export default support;
